
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { extractVideoId } from "@/utils/youtube";
import { processVideo } from "@/services/videoService";
import { YouTubeUrlInput } from "./YouTubeUrlInput";

interface VideoInputProps {
  onUrlSubmit: (submitted: boolean, videoId: string, isProcessed?: boolean) => void;
}

export const VideoInput = ({ onUrlSubmit }: VideoInputProps) => {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const videoId = extractVideoId(url);
    if (!videoId) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid YouTube URL",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Immediately show the video and chat interface with loading state
    onUrlSubmit(true, videoId, false);

    try {
      // Process the video in the background
      await processVideo(videoId, url);
      toast({
        title: "Success",
        description: "Video ready for chat!",
      });
      // Signal that processing is complete
      onUrlSubmit(true, videoId, true);
    } catch (error) {
      console.error('Failed to process video:', error);
      toast({
        title: "Warning",
        description: "Chat is ready but transcript processing is taking longer than expected. You can start chatting while we finish processing.",
        variant: "default",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 w-full animate-fade-in">
      <YouTubeUrlInput 
        url={url}
        onChange={setUrl}
        disabled={isLoading}
      />
      <Button 
        type="submit" 
        className="h-12 bg-primary hover:bg-primary/90 transition-all duration-300 min-w-[120px] text-white font-medium"
        disabled={!url || isLoading}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing
          </div>
        ) : (
          'Submit'
        )}
      </Button>
    </form>
  );
};
